import { EpgChannelGenre } from './EpgChannelGenre';
import { EpgProgram } from './EpgProgram';
import { RequestedProgramType } from './EpgProgramRequestQueue';
import { ChannelType, Consts, ProgramState } from './index';
import { Logger } from './Logger';
import { Observer } from './Observer';
import { PropertyHolder } from './PropertyHolder';

const MIN_OFFSET = 305; // sec
const MAX_OFFSET = 600; // sec


export interface ITypeChannelsCollection {
  [key: number]: EpgChannel;
}
export type TypeOrderedChannelsCollection = Array<{
  id: number;
  title: string;
  channels: Array<EpgChannel['id']>;
}>;
export interface ITypeMegogoIdMap {
  [key: number]: string;
}
export interface ITypeChannelsCollectionByGenres {
  [genre: number]: EpgChannel[];
}
export interface ITypeProgramsByDay {
  [dayTimestamp: number]: EpgProgram[];
}
export interface ILoadProgramsForChannelEvent {
  id: number;
  timestamp: number;
  period: number; // 8 hours in seconds
  direction: 'past' | 'future';
  resolve?: (...args: any[]) => void;
  reject?: (...args: any[]) => void;
  exclusiveRequest?: boolean;
}

interface IProgramCache {
  [timestamp: number]: EpgProgram | null;
}

// tslint:disable-next-line: max-classes-per-file
export class EpgChannel {
  public get hasExtendedProgram(): boolean {
    return this._hasExtendedProgram;
  }

  public get programUpdated(): boolean {
    return this._programUpdated;
  }

  public get id(): number {
    return this._id;
  }

  public get title(): string {
    return this._title;
  }

  public get type(): ChannelType {
    return this._type;
  }

  public get image(): string {
    return this._image;
  }

  public get programs(): EpgProgram[] | null {
    return this._programs;
  }

  public get currentProgram(): EpgProgram | null {
    let result: EpgProgram | null = null;
    if (
      this._currentProgramIndex >= 0 &&
      this._programs &&
      this._currentProgramIndex < this._programs.length
    ) {
      result = this._programs[this._currentProgramIndex];
    }
    return result;
  }

  public get currentProgramIndex(): number {
    return this._currentProgramIndex;
  }

  public get programsByDay(): ITypeProgramsByDay {
    return this._programsByDay;
  }

  public get isCurrent(): boolean {
    return this._isCurrent;
  }

  public set isCurrent(value: boolean) {
    this._isCurrent = value;
    if (value) {
      // force current program updating
      this.onTimeUpdate();

      const currentProgram = this.currentProgram;
      PropertyHolder.set(Consts.PROPERTY_CURRENT_PROGRAM, currentProgram);
      Observer.emit(Consts.EVENT_CURRENT_PROGRAM_CHANGED, currentProgram);
      if (this._type !== ChannelType.REGULAR_CHANNEL) {
        PropertyHolder.set(Consts.PROPERTY_PLAYING_PROGRAM, currentProgram);
      }
    } else {
      this._activeProgram = null;
    }
  }

  public get isLocked(): boolean {
    return this._isLocked;
  }

  public get isChannelControlsRestricted(): boolean {
    return this._isChannelControlsRestricted;
  }

  public get subscriptionId(): number {
    return this._subscriptionId;
  }

  public set originalGenre(value: EpgChannelGenre) {
    this._originalGenre = value;
  }

  public get originalGenre(): EpgChannelGenre {
    return this._originalGenre!;
  }

  public set isFavorite(value: boolean) {
    this._isFavorite = value;
  }

  public get isFavorite(): boolean {
    return this._isFavorite;
  }

  public set isFeatured(value: boolean) {
    this._isFeatured = value;
  }

  public get isFeatured(): boolean {
    return this._isFeatured;
  }
  public isInteractive: boolean;
  private _id: number;
  private _title: string;
  private _isFavorite: boolean;
  private _isFeatured: boolean;
  private _originalGenre?: EpgChannelGenre;
  private _type: ChannelType;
  private _image: string;
  private _programs: EpgProgram[] = [];
  private _currentProgramIndex: number;
  private _isCurrent: boolean = false;
  private _programsByDay: ITypeProgramsByDay;
  private _hasExtendedProgram: boolean = false;
  private _programUpdated: boolean = false;
  private _updatedProgramListRequested: boolean = false;
  private _extendedProgramListRequested: boolean = false;
  private _programsByTimestamp: any = {};
  private _isInitProgramFound: boolean = false;
  private _isLocked: boolean = false;
  private _isChannelControlsRestricted: boolean = false;
  private _subscriptionId: number;
  private _requestedTimestamps: number[];
  private _nextProgramCache: IProgramCache = {};
  private _prevProgramCache: IProgramCache = {};
  private _activeProgram: EpgProgram | null = null;
  private _updateTimeOffset: number;
  private _lastFailedSetPrograms = 0;
  private _isProgramAllocated = false;

  private readonly _onTimeUpdateCallback = this.onTimeUpdate.bind(this);
  private readonly _onNeedToLoadExtendedProgram = this.onNeedToLoadExtendedProgram.bind(
    this,
  );

  constructor(data: any, genre?: EpgChannelGenre) {
    this._id = data.id;
    this._title = data.title || '';
    this._type = this.getChannelType(data);
    this._isFavorite = data.is_favorite;
    this._image = data.image ? data.image.small || '' : '';
    this._isLocked = !data.is_available && !data.free;
    this._isChannelControlsRestricted = data.channel_controls_restricted;
    this._subscriptionId = data?.purchase_info?.svod?.subscriptions?.[0];
    this._requestedTimestamps = [];
    this._updateTimeOffset = Math.max(MIN_OFFSET, Math.random() * MAX_OFFSET);
    if (genre) {
      this._originalGenre = genre;
    }
    Observer.on(
      Consts.EVENT_NEED_TO_LOAD_EXTENDED_PROGRAM_LIST,
      this._onNeedToLoadExtendedProgram,
    );
    this.isInteractive =
      this._type === ChannelType.DVR_CHANNEL ||
      this._type === ChannelType.VOD_CHANNEL;
  }

  public dispose() {
    Observer.off(Consts.EVENT_TIME_UPDATE, this._onTimeUpdateCallback);
    Observer.off(
      Consts.EVENT_NEED_TO_LOAD_EXTENDED_PROGRAM_LIST,
      this._onNeedToLoadExtendedProgram,
    );
  }

  public get isProgramAllocated(){
    return this._isProgramAllocated;
  }

  public set isProgramAllocated(newVal){
    this._isProgramAllocated = newVal;
  }

  public setPrograms(programs: EpgProgram[], isExtended: boolean = false) {
    if (programs && programs.length > 0) {
      this._updatedProgramListRequested = false;
      let isHasNewPrograms = false;

      // add to this._programs only programs that absent in that array
      for (let i: number = 0, l: number = programs.length; i < l; ++i) {
        if (!(programs[i].startTimestamp in this._programsByTimestamp)) {
          isHasNewPrograms = true;
          this._programs.push(programs[i]);
          this._programsByTimestamp[programs[i].startTimestamp] = programs[i];
          programs[i].channel = this;
        }
      }
      if (!isHasNewPrograms) {
        this._lastFailedSetPrograms = Date.now();
      }
      this._programs.sort((a: EpgProgram, b: EpgProgram) => {
        return a.startTimestamp - b.startTimestamp;
      });

      this._programsByDay = this.createProgramsByDay(this._programs);

      this._prevProgramCache = this.clearNullieProgramCache(
        this._prevProgramCache,
      );
      this._nextProgramCache = this.clearNullieProgramCache(
        this._nextProgramCache,
      );

      Observer.on(Consts.EVENT_TIME_UPDATE, this._onTimeUpdateCallback);
      this._programUpdated = true;
    }
    if (isExtended && this._extendedProgramListRequested) {
      this._extendedProgramListRequested = false;
      this._hasExtendedProgram = true;
    }
    if (this._isCurrent) {
      this.onTimeUpdate();
      Observer.emit(Consts.EVENT_CURRENT_PROGRAM_LIST_UPDATED, this);
    }
  }

  public getNextProgram(program: EpgProgram): EpgProgram | null {
    let result: EpgProgram | null = null;
    if (program) {
      if (program.startTimestamp in this._nextProgramCache) {
        return this._nextProgramCache[program.startTimestamp];
      } else if (this._programs) {
        const index = this._programs.findIndex(
          (elem) => elem.startTimestamp === program.startTimestamp,
        );
        if (index > -1) {
          if (index + 1 < this._programs.length) {
            result = this._programs[index + 1];
          }
          if (index >= this._programs.length - 3 && this._programs.length > 7) { // 7 взято от балды
            // if left 3 programs
            const timestamp = this._programs[this._programs.length - 1]
              .endTimestamp;
            this.sendLoadProgramsEventIfNeeds(timestamp, 'future');
          }
        }
      }
      this._nextProgramCache = this.addProgramToCache(
        this._nextProgramCache,
        program.startTimestamp,
        result,
      );
    }
    return result;
  }

  public getPrevProgram(program: EpgProgram): EpgProgram | null {
    let result: EpgProgram | null = null;
    if (program) {
      if (program.startTimestamp in this._prevProgramCache) {
        return this._prevProgramCache[program.startTimestamp];
      } else if (this._programs) {
        const index = this._programs.findIndex(
          (elem) => elem.startTimestamp === program.startTimestamp,
        );
        if (index > -1) {
          if (index - 1 >= 0) {
            result = this._programs[index - 1];
          }
          if (index < 3 && this._programs.length > 7) { // 7 взято от балды
            // try to send programs request if left less than 3 programs
            const timestamp = this._programs[0].startTimestamp;
            this.sendLoadProgramsEventIfNeeds(timestamp, 'past');
          }
        }
      }
      this._prevProgramCache = this.addProgramToCache(
        this._prevProgramCache,
        program.startTimestamp,
        result,
      );
    }
    return result;
  }

  public toggleFavoriteCategory(state: boolean) {
    this._isFavorite = state;
  }

  public currentProgramTitle(length: number): string {
    let title = '';

    if (this._programUpdated) {
      if (this._programs.length) {
        title = this.currentProgram
          ? this.currentProgram.getTitleByLength(length)
          : '';
      } else {
        title = this._originalGenre ? this._originalGenre.title : '';
      }
    }

    return title;
  }

  public get activeProgram(): EpgProgram | undefined {
    return this._activeProgram || this._programs[this._currentProgramIndex];
  }

  public _setActiveProgram(program: EpgProgram) {
    this._activeProgram = program;
  }

  private sendLoadProgramsEventIfNeeds = (
    timestamp: number,
    direction: 'past' | 'future',
  ) => {
    if (!this._requestedTimestamps.includes(timestamp)) {
      this._requestedTimestamps.push(timestamp);
      const data: ILoadProgramsForChannelEvent = {
        id: this._id,
        timestamp,
        period: 28800, // 8 hours in seconds
        direction,
      };
      Observer.emit(Consts.EVENT_LOAD_PROGRAMS_FOR_CHANNEL, data);
    }
  }

  private getChannelType(data: any): ChannelType {
    let result: ChannelType;
    if (data && data.vod_channel) {
      result = ChannelType.VOD_CHANNEL;
    } else if (data && data.is_dvr) {
      result = ChannelType.DVR_CHANNEL;
    } else {
      result = ChannelType.REGULAR_CHANNEL;
    }
    return result;
  }

  private onTimeUpdate() {
    if (this._programs && this._programs.length > 0) {
      const currentTimestamp: number = PropertyHolder.get(
        Consts.PROPERTY_CURRENT_TIMESTAMP,
      );
      let i: number = /*this._currentProgramIndex >= 0 ? this._currentProgramIndex :*/ 0;
      let state: ProgramState;
      for (const l: number = this._programs.length; i < l; ++i) {
        state = this._programs[i].checkState(currentTimestamp);
        if (state === ProgramState.CURRENT && this._currentProgramIndex !== i) {
          Logger.log(
            'NEW CURRENT PROGRAM. CHANNEL:',
            this.title,
            'PROGRAM:',
            this._programs[i].title,
          );
          this._currentProgramIndex = i;
          if (this._isInitProgramFound) {
            Observer.emit(Consts.EVENT_ACTIVE_PROGRAM_CHANGED, this);
          } else {
            this._isInitProgramFound = true;
          }
          if (this._isCurrent) {
            PropertyHolder.set(
              Consts.PROPERTY_CURRENT_PROGRAM,
              this._programs[i],
            );
            Observer.emit(
              Consts.EVENT_CURRENT_PROGRAM_CHANGED,
              this._programs[i],
            );
            if (
              this._type !== ChannelType.REGULAR_CHANNEL &&
              !PropertyHolder.get(Consts.PROPERTY_PLAYING_PROGRAM)
            ) {
              PropertyHolder.set(
                Consts.PROPERTY_PLAYING_PROGRAM,
                this._programs[i],
              );
            }
          }
        }
      }

      if (
        this._currentProgramIndex === this._programs.length - 1 &&
        !this._updatedProgramListRequested &&
        this._programs[this._currentProgramIndex].isEnding(currentTimestamp, this._updateTimeOffset) &&
        Date.now() - this._lastFailedSetPrograms > 60000
      ) {
        this._updatedProgramListRequested = true;
        const data: any = { channel: this };
        data.type = this._isCurrent
          ? RequestedProgramType.SIXTEEN_HOURS
          : RequestedProgramType.ONE_HOUR;
        Observer.emit(Consts.EVENT_NEED_TO_LOAD_UPDATED_PROGRAM_LIST, data);
      }
    }
  }

  private onNeedToLoadExtendedProgram(event: any) {
    if (
      event.data &&
      event.data.channel &&
      event.data.channel.id === this._id
    ) {
      this._updatedProgramListRequested = true;
      this._extendedProgramListRequested = true;
    }
  }

  private createProgramsByDay(programs: EpgProgram[]): ITypeProgramsByDay {
    const result: ITypeProgramsByDay = {};
    let startDayTimestamp: number;
    let date: Date;
    for (let i: number = 0, l: number = programs.length; i < l; ++i) {
      date = new Date(programs[i].startTimestamp * 1000);
      date.setHours(0, 0, 0, 0);
      startDayTimestamp = Math.floor(date.getTime() / 1000);
      if (!result[startDayTimestamp]) {
        result[startDayTimestamp] = [];
      }
      result[startDayTimestamp].push(programs[i]);
    }

    // delete the first day if there is only one program
    try {
      let minTimestamp: number = Math.floor(new Date().getTime() / 1000);
      let parsedTimestamp;
      Object.keys(result).forEach((timestamp) => {
        parsedTimestamp = parseInt(timestamp, 10);
        if (parsedTimestamp < minTimestamp) {
          minTimestamp = parsedTimestamp;
        }
      });
      if (result[minTimestamp].length === 1) {
        delete result[minTimestamp];
      }
    } catch (error) {
      console.error(error);
    }

    return result;
  }

  private addProgramToCache = (
    cache: IProgramCache,
    key: number,
    program: EpgProgram | null,
  ): IProgramCache => {
    return {
      ...cache,
      [key]: program,
    };
  }

  private clearNullieProgramCache = (cache: IProgramCache): IProgramCache => {
    const newCache: IProgramCache = {};
    Object.entries(cache).forEach(([key, value]) => {
      if (value) {
        newCache[Number(key)] = value;
      }
    });
    return newCache;
  }
}
